import { Box, Link, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { ReactElement, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { imageBase } from '../../api/client';
import { useMarketTerm } from '../../hooks/useMarketTerm';
import { TOTAL_SERIES, getManufacturerSummary, getUtilizationSeries, utilizationStats } from '../../redux/slices/tools/toolsSlice';
import { UtilizationSparkline } from './UtilizationSparkline';
import { NOT_TRACKED_STATUSES, TRACKED_STATUSES, itemsLink } from './itemsLink';

export const formatPct = (value: number): string => `${value.toFixed(value < 10 && value > 0 ? 1 : 0)}%`;

export const cellSx = { py: 1.4, px: 2, fontSize: 13, whiteSpace: 'nowrap' };
export const headSx = { ...cellSx, fontSize: 12 };

type ManufacturerSummaryProps = { fetching: boolean; category: string };

// One row per manufacturer, narrowed to the product category selected in the
// equipment table above (or every category when that selection is Total). Every
// count links into the item list carrying the same manufacturer, category and
// tracking state the cell represents. The card around it, with the view toggle,
// is BreakdownSummary.
export const ManufacturerSummary = ({ fetching, category }: ManufacturerSummaryProps): ReactElement => {
  const term = useMarketTerm();
  const summary = useSelector(getManufacturerSummary);
  const series = useSelector(getUtilizationSeries);

  const rows = useMemo(() => summary(category), [summary, category]);
  const scope = category === TOTAL_SERIES ? undefined : category;

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ ...headSx, width: 64 }} />
          <TableCell sx={headSx}>Manufacturer</TableCell>
          <TableCell sx={headSx} align="right">
            Products
          </TableCell>
          <TableCell sx={headSx} align="right">
            Items
          </TableCell>
          <TableCell sx={headSx} align="right">
            {term('Tracked')}
          </TableCell>
          <TableCell sx={headSx} align="right">
            {term('Not Tracked')}
          </TableCell>
          <TableCell sx={headSx} align="right">
            {term('Tracked')} %
          </TableCell>
          <TableCell sx={headSx} align="right">
            Max %
          </TableCell>
          <TableCell sx={headSx} align="right">
            Avg %
          </TableCell>
          <TableCell sx={{ ...headSx, width: '30%' }}>Utilization, past 12 months</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {!fetching && !rows.length && (
          <TableRow>
            <TableCell sx={cellSx} colSpan={10}>
              No manufacturers in this category.
            </TableCell>
          </TableRow>
        )}
        {rows.map((row) => {
          const points = series.byManufacturer[row.manufacturerId]?.[category] || [];
          const stats = utilizationStats(points);
          return (
            <TableRow key={row.manufacturerId} hover>
              <TableCell sx={{ ...cellSx, width: 64 }}>
                {!!row.imageKey && (
                  // Logos vary from square to very wide, so the cell caps both
                  // dimensions and letterboxes rather than stretching a row.
                  <Box
                    component="img"
                    src={`${imageBase(row.imageKey)}?height=48`}
                    alt=""
                    sx={{ height: 24, maxWidth: 56, objectFit: 'contain', display: 'block' }}
                  />
                )}
              </TableCell>
              <TableCell sx={cellSx}>
                <Link component={RouterLink} to={`/tools/manufacturers/${row.manufacturerId}/details`} underline="hover" sx={{ fontSize: 13 }}>
                  {row.manufacturerName}
                </Link>
              </TableCell>
              <TableCell sx={cellSx} align="right">
                {row.products.toLocaleString()}
              </TableCell>
              <TableCell sx={cellSx} align="right">
                <Link component={RouterLink} to={itemsLink({ category: scope, manufacturerId: row.manufacturerId })} underline="hover" color="inherit">
                  {row.items.toLocaleString()}
                </Link>
              </TableCell>
              <TableCell sx={cellSx} align="right">
                <Link
                  component={RouterLink}
                  to={itemsLink({ category: scope, manufacturerId: row.manufacturerId, scanStatus: TRACKED_STATUSES })}
                  underline="hover"
                  color="inherit"
                >
                  {row.tracked.toLocaleString()}
                </Link>
              </TableCell>
              <TableCell sx={cellSx} align="right">
                <Link
                  component={RouterLink}
                  to={itemsLink({ category: scope, manufacturerId: row.manufacturerId, scanStatus: NOT_TRACKED_STATUSES })}
                  underline="hover"
                  color="inherit"
                >
                  {row.notTracked.toLocaleString()}
                </Link>
              </TableCell>
              <TableCell sx={cellSx} align="right">
                {formatPct(row.trackedPct)}
              </TableCell>
              <TableCell sx={cellSx} align="right">
                {stats ? formatPct(stats.max) : '—'}
              </TableCell>
              <TableCell sx={cellSx} align="right">
                {stats ? formatPct(stats.avg) : '—'}
              </TableCell>
              <TableCell sx={{ ...cellSx, px: 2, width: '30%' }}>
                <UtilizationSparkline points={points} />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
